import type { PlanName } from "@prisma/client";
import type { NotionDataRes } from "~/server/api/notion/data.get";
import type { UserSubscribeReqBody } from "~/server/api/user/getCheckoutUrl.post";
import type { SUser } from "~/server/services/user/queries";
import { ProductName } from "@prisma/client";

export const useUserStore = defineStore("user", () => {
  // State
  const data = ref<SUser>();
  const notion = ref<NotionDataRes>();

  // Getters
  const { ready, loggedIn, clear } = useUserSession();
  const isAuthenticated = computed(() => ready.value && loggedIn.value);
  const isUserDataFetched = computed(() => !!data.value);
  const canUseApp = computed(() => !!data.value?.purchases.some(p => p.active && p.product.name === ProductName.NOTION_FINANCE_TRACKER));

  // User data actions
  async function fetchData() {
    try {
      const userDataReq = $fetch("/api/user/data", {
        method: "GET",
        headers: getHeaders(),
      });

      const notionDataReq = $fetch("/api/notion/data", {
        method: "GET",
        headers: getHeaders(),
      });

      const [userData, notionData] = await Promise.all([userDataReq, notionDataReq]);
      // We need to convert the endsAt and createdAt dates to a Date object because we get them as string from the server
      data.value = {
        ...userData,
        usages: userData.usages.map(u => ({
          ...u,
          createdAt: new Date(u.createdAt),
        })),
        subscription: userData.subscription
          ? {
              ...userData.subscription,
              endsAt: new Date(userData.subscription.endsAt),
            }
          : null,
      };

      notion.value = notionData ?? undefined;
    }
    catch (error) {
      console.error(error);
      throw createError({ statusMessage: "Failed to fetch user data.", statusCode: 400 });
    }
  }

  // Auth actions
  async function signIn(callbackUrl = "/app/user") {
    const redirect = useCookie("redirectUrl", { maxAge: 1 * 120 });
    redirect.value = callbackUrl;
    await navigateTo("/auth/google", { external: true });
  }

  async function signOut() {
    await clear();
    $reset();
    await navigateTo("/");
  }

  // Subscription actions
  async function openCheckoutModal(planOrProduct: PlanName | ProductName, billYearly = false) {
    const body: UserSubscribeReqBody = {
      planOrProduct,
      billYearly,
      embed: true,
    };

    try {
      const url = await $fetch("/api/user/getCheckoutUrl", {
        method: "POST",
        headers: getHeaders(),
        body,
      });
      window.LemonSqueezy.Url.Open(url);
      return url;
    }
    catch (error) {
      console.error(error);
      throw createError({ statusMessage: "Failed to fetch checkout url.", statusCode: 400 });
    }
  }

  async function openCustomerPortal() {
    try {
      const url = await $fetch("/api/user/getCustomerPortalUrl", {
        method: "GET",
        headers: getHeaders(),
      });
      window.LemonSqueezy.Url.Open(url);
      return url;
    }
    catch (error) {
      console.error(error);
      throw createError({ statusMessage: "Failed to fetch customer portal url.", statusCode: 400 });
    }
  }

  // Reset store
  function $reset() {
    data.value = undefined;
    notion.value = undefined;
  }

  return {
    data,
    notion,

    isAuthenticated,
    isUserDataFetched,
    canUseApp,

    fetchData,

    signIn,
    signOut,

    openCheckoutModal,
    openCustomerPortal,

    $reset,
  };
});

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
